import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { UserService } from './user.service';
import { Firestore, collection, getCountFromServer, onSnapshot, query, where } from 'firebase/firestore';
import { FIREBASE_FIRESTORE } from '@awo-care/shared-firebase';

@Injectable({ providedIn: 'root' })
export class ChangesBadgeService {
  private readonly firestore = inject(FIREBASE_FIRESTORE);
  private readonly destroyRef = inject(DestroyRef);
  private readonly userService = inject(UserService);

  readonly alterationsCount = signal(0);
  readonly exportCount = signal(0);
  readonly unreadTicketsCount = signal(0);
  readonly duplicatesCount = signal(0);

  private ticketListenerActive = false;
  private alterationsListenerActive = false;
  private duplicatesListenerActive = false;

  async refreshCounts(): Promise<void> {
    // Vor dem Export-Zähler: Mitarbeiter und Teamleitung dürfen die Exporte nicht
    // lesen, ihr Ticket-Zähler (eigene Tickets, #1277) muss trotzdem starten.
    this.listenToUnreadTickets();
    try {
      const exports = await this.countExports();
      this.exportCount.set(exports);
      this.listenToSubmittedAlterations();
      this.listenToDuplicates();
    } catch {
      // Silently ignore — badge counts are non-critical
    }
  }

  /**
   * Offene Doubletten-Cluster (#710): Verdachtsfälle ohne Erledigt-Markierung,
   * gezählt als Cluster (nicht als Einzel-Schüler). Server-seitig gefiltert
   * (2× Equality, kein Composite-Index nötig) — jedes suspect Mitglied trägt
   * seit User-Feedback 2026-07-10 IMMER ein explizites duplicateDismissed-Feld.
   */
  private listenToDuplicates(): void {
    if (this.duplicatesListenerActive) return;
    this.duplicatesListenerActive = true;

    const q = query(
      collection(this.firestore, 'students'),
      where('duplicateSuspect', '==', true),
      where('duplicateDismissed', '==', false)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const openClusters = new Set<string>();
        for (const doc of snapshot.docs) {
          const clusterKey = doc.data()['duplicateClusterKey'];
          if (typeof clusterKey === 'string' && clusterKey) {
            openClusters.add(clusterKey);
          }
        }
        this.duplicatesCount.set(openClusters.size);
      },
      () => {
        this.duplicatesListenerActive = false;
      }
    );

    this.destroyRef.onDestroy(() => unsubscribe());
  }

  private listenToUnreadTickets(): void {
    if (this.ticketListenerActive) return;
    this.ticketListenerActive = true;

    // Mitarbeiter und Teamleitung dürfen nur eigene Tickets lesen (Rules, #1277):
    // die Query braucht dann den assignee-Filter, "closed" fällt client-seitig raus.
    const ownUid = this.userService.currentUser()?.uid;
    const onlyOwn = this.userService.isEmployee() || this.userService.isTeamlead();
    const q = onlyOwn
      ? query(collection(this.firestore, 'tickets'), where('assignee.uid', '==', ownUid ?? ''))
      : query(collection(this.firestore, 'tickets'), where('status', '!=', 'closed'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const uid = this.userService.currentUser()?.uid;
        if (!uid) {
          this.unreadTicketsCount.set(0);
          return;
        }

        let count = 0;
        for (const doc of snapshot.docs) {
          const data = doc.data();
          if (data['status'] === 'closed') continue;

          // Assigned to someone else → skip
          if (data['assignee']?.uid && data['assignee'].uid !== uid) continue;

          const lastActivity = data['lastActivityOn'];
          if (!lastActivity) continue;

          const lastSeen = data['lastSeenBy']?.[uid];
          if (!lastSeen) {
            count++;
            continue;
          }

          if (lastActivity.toMillis() > lastSeen.toMillis()) {
            count++;
          }
        }
        this.unreadTicketsCount.set(count);
      },
      () => {
        this.ticketListenerActive = false;
      }
    );

    this.destroyRef.onDestroy(() => unsubscribe());
  }

  private listenToSubmittedAlterations(): void {
    if (this.alterationsListenerActive) return;
    this.alterationsListenerActive = true;

    const q = query(
      collection(this.firestore, 'alterations'),
      where('status', '==', 'submitted')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        this.alterationsCount.set(snapshot.size);
      },
      () => {
        this.alterationsListenerActive = false;
      }
    );

    this.destroyRef.onDestroy(() => unsubscribe());
  }

  private async countExports(): Promise<number> {
    const q = query(
      collection(this.firestore, 'exports'),
      where('isExported', '==', false)
    );
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  }
}
